import { APP_TABS, APP_TAB_IDS } from './navigation';

/**
 * Реестр панелей интерфейса — единственное место, где записано название панели по умолчанию.
 *
 * Компонент своего литерала после подключения к реестру не держит: иначе реестр и JSX
 * разъезжаются. Это не гипотетическая беда — ровно так уже разошёлся `texts.json`, где
 * «Эффективность сотрудников» против «Эффективность операторов» в разметке.
 *
 * `id` — часть контракта: он уходит в `app_settings` и переживает рефакторинг компонента.
 * Переименование панели администратором его не меняет, поэтому менять id при правке кода
 * нельзя — настройка тенанта потеряется молча.
 *
 * Префикс `ui:` отделяет статические панели от карточек пакета узлов анализа
 * (`{node_id}.{output_key}`), у которых своё переименование в «Узлы анализа». Хранилища и так
 * разные, но префикс делает границу видимой и проверяется сервером (`IsValidPanelId`).
 */

/**
 * Синтетическая вкладка для верхней панели.
 *
 * `Dashboard.jsx` рендерится над семью разделами сразу (`App.js`: исключены только «Настройки»,
 * «Пропущенные» и «Фильтры номеров»), поэтому скрыть «Всего звонков» — значит скрыть её везде.
 * Положить эти плитки в группу «Общая статистика» значило бы соврать администратору о том, где
 * подействует галка.
 */
export const SHARED_PANEL_TAB = 'shared';

export const PANEL_TAB_IDS = [SHARED_PANEL_TAB, ...APP_TAB_IDS];

const APP_TAB_LABELS = new Map(APP_TABS.map(({ id, label }) => [id, label]));

/** Подпись группы в настройках. Для разделов берётся из `APP_TABS`, чтобы они не разошлись. */
export const panelTabLabel = (tabId) =>
  tabId === SHARED_PANEL_TAB
    ? 'Верхняя панель (над всеми разделами)'
    : APP_TAB_LABELS.get(tabId) ?? tabId;

/**
 * Панели. Поля:
 * - `id` — `ui:{tab}.{slug}`, стабильный и уникальный;
 * - `tab` — вкладка приложения или `shared`;
 * - `title` / `description` — умолчания; `description` отсутствует там, где подпись
 *   динамическая (считает звонки или подставляет период) и переименованию не поддаётся;
 * - `group: 'kpi'` — плитка, а не карточка: в настройках идёт отдельным блоком;
 * - `group: 'column'` — столбец таблицы раздела: тоже отдельным блоком, подписи у него нет,
 *   порядок совпадает с порядком столбцов в таблице (сверяет тест модуля столбцов);
 * - `renameOnly: true` — галки видимости нет. Так помечены карточки-шапки, в теле которых
 *   живут фильтры раздела: спрятать такую карточку значит отобрать у пользователя фильтры;
 * - `source` — путь от `src/`, по нему сторожевой тест проверяет, что компонент действительно
 *   подключён к реестру, а не оставил себе литерал.
 */
export const PANELS = [
  // --- Верхняя панель, общая для всех разделов --------------------------------------------
  {
    id: 'ui:shared.kpi-total-calls',
    tab: SHARED_PANEL_TAB,
    group: 'kpi',
    title: 'Всего звонков',
    description: 'Количество вызовов за период',
    source: 'components/Dashboard.jsx'
  },
  {
    id: 'ui:shared.kpi-avg-duration',
    tab: SHARED_PANEL_TAB,
    group: 'kpi',
    title: 'Время разговора',
    description: 'Средняя длительность разговора без времени ожидания и других частей звонка, в формате минуты:секунды.',
    source: 'components/Dashboard.jsx'
  },
  {
    id: 'ui:shared.kpi-checklist',
    tab: SHARED_PANEL_TAB,
    group: 'kpi',
    title: 'Чек-лист',
    description: 'Средняя оценка звонков по чек-листам за выбранный период, по шкале от 0 до 10.',
    source: 'components/Dashboard.jsx'
  },
  {
    id: 'ui:shared.kpi-active-employees',
    tab: SHARED_PANEL_TAB,
    group: 'kpi',
    title: 'Операторы',
    description: 'Количество операторов, у которых были звонки за выбранный период. Это не текущий онлайн-статус.',
    source: 'components/Dashboard.jsx'
  },
  {
    id: 'ui:shared.kpi-minutes-used',
    tab: SHARED_PANEL_TAB,
    group: 'kpi',
    title: 'Использовано минут',
    description: 'Суммарная длительность завершённых обработок с начала периода учёта. Лимит и период учёта задаются в разделе «Интеграции». Показатель не зависит от выбранных дат и направления звонков.',
    source: 'components/Dashboard.jsx'
  },

  // --- Общая статистика --------------------------------------------------------------------
  {
    id: 'ui:dashboard.call-dynamics',
    tab: 'dashboard',
    title: 'Динамика звонков',
    source: 'components/GeneralStatistics.jsx'
  },
  {
    id: 'ui:dashboard.calls-by-employee',
    tab: 'dashboard',
    title: 'Салоны и общая линия',
    description: 'Куда звонят клиенты',
    source: 'components/CallsByEmployeeChart.jsx'
  },

  // --- Сотрудники --------------------------------------------------------------------------
  {
    id: 'ui:employees.header',
    tab: 'employees',
    renameOnly: true,
    title: 'Сотрудники',
    description: 'Аналитика эффективности сотрудников',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.kpi-avg-calls',
    tab: 'employees',
    group: 'kpi',
    title: 'В среднем звонков',
    description: 'В расчёте на сотрудника',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.kpi-avg-duration',
    tab: 'employees',
    group: 'kpi',
    title: 'Средняя длительность',
    description: 'Минуты/секунды',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.kpi-avg-talk',
    tab: 'employees',
    group: 'kpi',
    title: 'Среднее время разговора',
    description: 'Чистое общение',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.kpi-avg-wait',
    tab: 'employees',
    group: 'kpi',
    title: 'Среднее ожидание',
    description: 'Дозвон и удержание',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.kpi-avg-checklist',
    tab: 'employees',
    group: 'kpi',
    title: 'Средний чек-лист',
    description: 'Балл по качеству',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.kpi-workload',
    tab: 'employees',
    group: 'kpi',
    title: 'Нагрузка',
    description: 'Количество сотрудников',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.operator-efficiency',
    tab: 'employees',
    title: 'Эффективность операторов',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.quality-comparison',
    tab: 'employees',
    title: 'Сравнение качества',
    description: 'Средний балл каждого сотрудника против среднего по всем сотрудникам за период',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.quality-trend',
    tab: 'employees',
    title: 'Динамика качества работы',
    description: 'Средний балл по каждому сотруднику в выбранном диапазоне',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.calls-trend',
    tab: 'employees',
    title: 'Динамика звонков по сотрудникам',
    description: 'Количество звонков по дням диапазона',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.weekday-workload',
    tab: 'employees',
    title: 'Нагрузка по дням недели',
    description: 'Сумма звонков среди выбранных сотрудников',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.hourly-workload',
    tab: 'employees',
    title: 'Нагрузка по часам',
    description: 'Всего звонков по часам',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.hourly-by-day',
    tab: 'employees',
    title: 'Часовая нагрузка по дням',
    description: 'Суммарная длительность звонков за день (минуты)',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.wait-hold-hourly',
    tab: 'employees',
    title: 'Ожидание и удержание по часам',
    description: 'Суммарное время ожидания и удержания по часам (минуты)',
    source: 'components/EmployeeAnalytics.jsx'
  },
  {
    id: 'ui:employees.forbidden-phrases',
    tab: 'employees',
    title: 'Запрещённые фразы',
    description: 'Клиентские звонки без внутренних коммуникаций',
    source: 'components/EmployeeAnalytics.jsx'
  },

  // --- Детали звонков ----------------------------------------------------------------------
  {
    id: 'ui:calls.header',
    tab: 'calls',
    renameOnly: true,
    title: 'Детали звонков',
    description: 'Подробная информация о звонках и их анализ',
    source: 'components/CallDetails.jsx'
  },
  {
    id: 'ui:calls.journal',
    tab: 'calls',
    title: 'Журнал звонков',
    source: 'components/CallDetails.jsx'
  },

  // --- Пропущенные -------------------------------------------------------------------------
  {
    id: 'ui:missed.header',
    tab: 'missed',
    renameOnly: true,
    title: 'Пропущенные звонки',
    description:
      'Входящие звонки без ответа, сгруппированные по клиенту и дню. Период выбирается '
      + 'в календаре вверху страницы, список обновляется автоматически раз в минуту.',
    source: 'components/MissedCalls.jsx'
  },
  {
    id: 'ui:missed.stat-visible',
    tab: 'missed',
    group: 'kpi',
    title: 'На экране',
    source: 'components/MissedCalls.jsx'
  },
  {
    id: 'ui:missed.stat-waiting',
    tab: 'missed',
    group: 'kpi',
    title: 'Ждут оператора',
    source: 'components/MissedCalls.jsx'
  },
  {
    id: 'ui:missed.stat-in-progress',
    tab: 'missed',
    group: 'kpi',
    title: 'В работе / дозвонились',
    source: 'components/MissedCalls.jsx'
  },
  {
    id: 'ui:missed.clients',
    tab: 'missed',
    title: 'Список клиентов',
    source: 'components/MissedCalls.jsx'
  },

  // --- Клиенты -----------------------------------------------------------------------------
  {
    id: 'ui:clients.header',
    tab: 'clients',
    renameOnly: true,
    title: 'Клиенты',
    description: 'База клиентов и история взаимодействий',
    source: 'components/Clients.jsx'
  },
  {
    id: 'ui:clients.stat-total',
    tab: 'clients',
    group: 'kpi',
    title: 'Клиентов',
    source: 'components/Clients.jsx'
  },
  {
    id: 'ui:clients.stat-calls-per-client',
    tab: 'clients',
    group: 'kpi',
    title: 'Звонков/клиент',
    source: 'components/Clients.jsx'
  },
  {
    id: 'ui:clients.stat-with-complaints',
    tab: 'clients',
    group: 'kpi',
    title: 'С жалобами',
    source: 'components/Clients.jsx'
  },
  {
    id: 'ui:clients.stat-repeat',
    tab: 'clients',
    group: 'kpi',
    title: 'Повторные',
    source: 'components/Clients.jsx'
  },
  {
    id: 'ui:clients.stat-repeat-complaints',
    tab: 'clients',
    group: 'kpi',
    title: 'Повторных жалоб',
    source: 'components/Clients.jsx'
  },
  {
    id: 'ui:clients.period',
    tab: 'clients',
    title: 'Клиенты за период',
    source: 'components/Clients.jsx'
  },

  // --- Жалобы ------------------------------------------------------------------------------
  {
    id: 'ui:complaints.header',
    tab: 'complaints',
    renameOnly: true,
    title: 'Жалобы',
    description: 'Разбор клиентских жалоб на уровне кейса «клиент + дата»',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.stat-unique',
    tab: 'complaints',
    group: 'kpi',
    title: 'Уникальных жалоб',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.stat-share',
    tab: 'complaints',
    group: 'kpi',
    title: 'Доля от обращений',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.stat-new',
    tab: 'complaints',
    group: 'kpi',
    title: 'Новых / неразобранных',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.stat-top-point',
    tab: 'complaints',
    group: 'kpi',
    title: 'Точка-лидер',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.period',
    tab: 'complaints',
    title: 'Жалобы за период',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.by-point',
    tab: 'complaints',
    title: 'Жалобы по точкам',
    description: 'Где чаще возникают клиентские проблемы. Клик по точке откроет её жалобы.',
    source: 'components/Complaints.jsx'
  },
  // Столбцы таблицы «Жалобы за период», в порядке таблицы (`complaints/caseColumns.js`).
  // Столбца «Подробнее» здесь нет намеренно: в нём кнопка «Разобрать», и без него кейс не
  // открыть. Скрытый «Тип жалобы» или «Детализация» уносит с собой и одноимённый фильтр.
  {
    id: 'ui:complaints.col-date',
    tab: 'complaints',
    group: 'column',
    title: 'Дата',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.col-client',
    tab: 'complaints',
    group: 'column',
    title: 'Клиент',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.col-calls',
    tab: 'complaints',
    group: 'column',
    title: 'Звонков',
    source: 'components/Complaints.jsx'
  },
  {
    // Рисуется только с интеграцией iiko — точка кейса берётся из заказа.
    id: 'ui:complaints.col-point',
    tab: 'complaints',
    group: 'column',
    title: 'Точка',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.col-type',
    tab: 'complaints',
    group: 'column',
    title: 'Тип жалобы',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.col-detail',
    tab: 'complaints',
    group: 'column',
    title: 'Детализация',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.col-compensation',
    tab: 'complaints',
    group: 'column',
    title: 'Компенсация',
    source: 'components/Complaints.jsx'
  },
  {
    id: 'ui:complaints.col-status',
    tab: 'complaints',
    group: 'column',
    title: 'Статус',
    source: 'components/Complaints.jsx'
  },

  // --- Интеграции ---------------------------------------------------------------------------
  // Видна только администратору. Гард видимости — у родителя (Integrations.jsx): карточка сама
  // опрашивает сервер по таймеру, и скрытая продолжала бы ходить в API.
  {
    id: 'ui:integrations.telephony-health',
    tab: 'integrations',
    title: 'Приём звонков',
    description:
      'Принимает ли сервер звонки от телефонии за последние сутки: последние итоги и записи, '
      + 'отказы с причиной (неверный ключ, подпись или токен), записи, которые не скачались, и '
      + 'отвеченные звонки без записи — так видно, что в кабинете телефонии выключена запись разговоров.',
    source: 'components/integrations/TelephonyIntakeHealthCard.jsx'
  },

  // --- Фильтры номеров ---------------------------------------------------------------------
  {
    id: 'ui:number-filters.list',
    tab: 'number-filters',
    // Раздел состоит из одной этой карточки, и в ней же живёт кнопка «Добавить номер»:
    // спрятать её значило бы оставить пустой раздел без способа вернуть список.
    renameOnly: true,
    title: 'Фильтры номеров',
    description:
      'Звонки с этих номеров и на эти номера не обрабатываются: задание создаётся сразу '
      + 'со статусом «Пропущено», запись при этом сохраняется. Обработать такой звонок '
      + 'вручную можно кнопкой «Переобработать» на вкладке «Задания».',
    source: 'components/NumberFilters.jsx'
  },

  // --- Типы и итоги звонков -----------------------------------------------------------------
  // Шапка и обзоры держат переключатель и выбор, с которых начинается весь разбор: без них
  // раздел потерял бы смысл, поэтому галки видимости у них нет.
  {
    id: 'ui:call-paths.header',
    tab: 'call-paths',
    renameOnly: true,
    title: 'Типы и итоги звонков',
    description: 'Два способа начать разбор: с типа обращения или с итога. В каждом режиме можно дойти до динамики, оценок чек-листа и конкретных звонков.',
    source: 'components/callTypes/CallTypesOutcomes.jsx'
  },
  {
    id: 'ui:call-paths.types',
    tab: 'call-paths',
    renameOnly: true,
    title: 'Типы обращений',
    description: 'Число, доля и динамика звонков каждого типа за период. Нажмите тип, чтобы открыть его итоги и разбор сотрудников; нажмите название под графиком, чтобы скрыть или показать линию.',
    source: 'components/callTypes/CallTypesOutcomes.jsx'
  },
  {
    id: 'ui:call-paths.outcomes',
    tab: 'call-paths',
    renameOnly: true,
    title: 'Итоги всех звонков',
    description: 'Число, доля и динамика каждого итога независимо от типа обращения. Нажмите итог, чтобы увидеть типы обращений с этим итогом и разбор сотрудников.',
    source: 'components/callTypes/CallTypesOutcomes.jsx'
  },
  {
    id: 'ui:call-paths.pair-trend',
    tab: 'call-paths',
    title: 'Динамика итога',
    description: 'Количество — звонки с выбранным итогом за период. Процент — сколько из звонков выбранного типа за тот же период получили этот итог.',
    source: 'components/callTypes/PairTrendChart.jsx'
  },
  {
    id: 'ui:call-paths.employees',
    tab: 'call-paths',
    title: 'Динамика сотрудников',
    description: 'Звонки, доля и балл чек-листа каждого сотрудника в выбранном срезе.',
    source: 'components/callTypes/EmployeeComparisonTrend.jsx'
  },
  {
    id: 'ui:call-paths.quality',
    tab: 'call-paths',
    title: 'Качество и сотрудники',
    description: 'Балл относится к звонкам с выбранным типом и итогом. Нажмите сотрудника, чтобы увидеть его звонки.',
    source: 'components/callTypes/EmployeeQualityCard.jsx'
  },
  {
    id: 'ui:call-paths.calls',
    tab: 'call-paths',
    title: 'Звонки с выбранным итогом',
    description: 'Первые звонки выбранной пары; полный список открывается в журнале.',
    source: 'components/callTypes/PairCallsCard.jsx'
  }
];

/** Идентификатор записи, хранящей название раздела в меню. */
export const tabLabelPanelId = (tabId) => `ui:${tabId}.tab-label`;

/**
 * Названия разделов в боковом меню. Переименовываются той же настройкой, что и панели, —
 * идентификаторы не пересекаются, поэтому хранилище общее.
 *
 * Выводятся из `APP_TABS`, а не перечисляются руками: иначе новый раздел появился бы в меню,
 * но не в настройках. Галки видимости у них нет — составом разделов управляют права учётной
 * записи (`allowedTabs`), и вторая точка управления тем же означала бы два разных ответа на
 * вопрос «почему я не вижу раздел».
 */
export const TAB_LABEL_PANELS = APP_TABS.map(({ id, label }) => ({
  id: tabLabelPanelId(id),
  tab: id,
  title: label,
  renameOnly: true,
  isTabLabel: true
}));

/**
 * Идентификаторы константами, чтобы компоненты не писали строку руками: опечатка в литерале
 * молча вернула бы умолчание, а опечатка в имени константы падает сборкой.
 */
export const PANEL_IDS = PANELS.reduce((accumulator, panel) => {
  accumulator[panel.id] = panel.id;
  return accumulator;
}, {});

export const PANELS_BY_ID = new Map(
  [...TAB_LABEL_PANELS, ...PANELS].map((panel) => [panel.id, panel])
);

export const getPanel = (panelId) => PANELS_BY_ID.get(panelId) ?? null;

/** Панели раздела в порядке объявления. Название раздела в меню сюда не входит. */
export const panelsForTab = (tabId) => PANELS.filter((panel) => panel.tab === tabId);

/**
 * Группы для экрана настроек: вкладки в порядке `PANEL_TAB_IDS`.
 *
 * Группа остаётся, даже если панелей у раздела нет: у «Настроек» и «Помощи» их и не должно
 * быть, а название раздела в меню переименовать всё равно нужно. Пустой остаётся только
 * группа верхней панели — у неё нет ни названия раздела, ни своих панелей, но такого случая
 * в реестре нет.
 */
export const panelGroups = () =>
  PANEL_TAB_IDS.map((tabId) => {
    const panels = panelsForTab(tabId);
    return {
      id: tabId,
      title: panelTabLabel(tabId),
      tabLabel: TAB_LABEL_PANELS.find((panel) => panel.tab === tabId) ?? null,
      kpi: panels.filter((panel) => panel.group === 'kpi'),
      cards: panels.filter((panel) => panel.group !== 'kpi' && panel.group !== 'column'),
      columns: panels.filter((panel) => panel.group === 'column')
    };
  }).filter(
    (group) =>
      group.tabLabel !== null
      || group.kpi.length > 0
      || group.cards.length > 0
      || group.columns.length > 0
  );
